// ── appUpdate.js — обновления приложения на телефоне (Android) ───────────────
// Приложение на телефоне содержит свою копию сайта. Чтобы не ставить новый APK
// на каждую правку, новая версия сайта скачивается сама: при сборке на Vercel
// scripts/make-ota.js кладёт на сайт ota/latest.json и архив с сайтом.
//
// Скачанная версия включается при следующем запуске приложения (до того, как
// что-то начнёт играть) или сразу — кнопкой «Обновить» в подсказке. Свёрнутое
// приложение само не перезагружается: это оборвало бы аудио или ввод карточки.
//
// Обновление — всегда до самой свежей версии, одним нажатием: кнопка сначала
// спрашивает сайт, нет ли версии новее скачанной, и если есть — качает и ставит её.
// При запуске скачанная версия ставится, только если она всё ещё последняя; иначе
// её пропускаем, и подсказка предложит сразу последнюю. Раньше при частых выпусках
// обновления шли цепочкой: поставил одно — через полчаса предлагалось следующее.
//
// Если новому сайту нужна более новая нативная часть, подсказка предлагает
// скачать APK (открывается в браузере телефона).
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { isNative } from './utils';

import { hasAppBridge, openExternal } from './nativeBridge';
import { t } from './i18n';
const SITE = 'https://audiocards.vercel.app';
const BUILD_ID = process.env.REACT_APP_BUILD_ID || 'dev';
const PENDING_KEY = 'ac_ota_pending';     // { id, version } — скачано, ждёт запуска
const DISMISS_KEY = 'ac_update_dismissed'; // версия, подсказку о которой закрыли
const CHECK_EVERY = 30 * 60 * 1000;

const readJson = (key) => { try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; } };
const writeJson = (key, v) => { try { v == null ? localStorage.removeItem(key) : localStorage.setItem(key, JSON.stringify(v)); } catch {} };

// "1.10.0" > "1.9.2"; нечисловые части считаются нулём.
export function isNewer(a, b) {
  const pa = String(a || '').split(/[.-]/).map((n) => parseInt(n, 10) || 0);
  const pb = String(b || '').split(/[.-]/).map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    if ((pa[i] || 0) !== (pb[i] || 0)) return (pa[i] || 0) > (pb[i] || 0);
  }
  return false;
}

async function nativeVersion() {
  try {
    const { version } = await CapacitorUpdater.getBuiltinVersion();
    if (version) return String(version);
  } catch {}
  try { return String((await CapacitorUpdater.current()).native || ''); } catch { return ''; }
}

// ── Подсказка вверху экрана (тёмная плашка — читается в любой теме) ───────────
function showBanner(key, text, action, onAction) {
  if (readJson(DISMISS_KEY) === key) return;
  document.getElementById('ac-update-banner')?.remove();
  const el = document.createElement('div');
  el.id = 'ac-update-banner';
  el.setAttribute('role', 'status');
  el.style.cssText = 'position:fixed;left:12px;right:12px;top:calc(env(safe-area-inset-top, 0px) + 10px);z-index:10001;' +
    'margin:0 auto;max-width:440px;display:flex;align-items:center;gap:10px;padding:10px 10px 10px 16px;' +
    'background:#1f2430;color:#fff;border-radius:14px;box-shadow:0 6px 24px rgba(0,0,0,.28);' +
    'font:500 14px/1.35 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;';
  const label = document.createElement('span');
  label.style.flex = '1';
  label.textContent = text;
  const btn = document.createElement('button');
  btn.textContent = action;
  btn.style.cssText = 'border:0;border-radius:10px;padding:8px 14px;background:#9db4ff;color:#14171f;font:inherit;font-weight:700;cursor:pointer;';
  btn.onclick = onAction;
  const close = document.createElement('button');
  close.textContent = '✕';
  close.setAttribute('aria-label', t("Закрыть"));
  close.style.cssText = 'border:0;background:none;color:#aab;font-size:16px;padding:6px 8px;cursor:pointer;';
  close.onclick = () => { writeJson(DISMISS_KEY, key); el.remove(); };
  el.append(label, btn, close);
  document.body.appendChild(el);
  return { el, label, btn };
}

// Что делать со скачанной при прошлом запуске версией: поставить, если она последняя
// (или узнать последнюю не вышло — нет сети), иначе пропустить: ставить устаревшую,
// чтобы через минуту предложить следующую, незачем.
export function pendingAction(pending, latestVersion, buildId) {
  if (!pending || pending.version === buildId) return 'none';
  if (latestVersion && latestVersion !== pending.version) return 'skip';
  return 'apply';
}

// Описание последней версии на сайте; null — нет сети или сайт не ответил за ms.
async function fetchLatest(ms = 0) {
  if (navigator.onLine === false) return null;
  const ctrl = ms && typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), ms) : null;
  try {
    const res = await fetch(`${SITE}/ota/latest.json?t=${Date.now()}`, { cache: 'no-store', signal: ctrl?.signal });
    return res.ok ? await res.json() : null;
  } catch { return null; } finally { if (timer) clearTimeout(timer); }
}

// Скачанная версия latest (или скачать её). Бросает ошибку, если скачать не вышло.
async function bundleFor(latest) {
  const { bundles = [] } = await CapacitorUpdater.list();
  const have = bundles.find((b) => b.version === latest.version && b.status !== 'error');
  return have || CapacitorUpdater.download({ url: latest.url, version: latest.version, checksum: latest.checksum });
}

// Кнопка «Обновить»: перед установкой ещё раз спросить сайт — вдруг уже есть версия новее
// скачанной. Пока качается — надпись на плашке, кнопка неактивна. Не вышло скачать
// новую — ставим ту, что уже скачана (она всё равно новее текущей).
async function updateToLatest(bundle, ui) {
  if (ui) { ui.label.textContent = t("Загружаю последнюю версию…"); ui.btn.disabled = true; ui.btn.style.opacity = '0.6'; }
  let target = bundle;
  try {
    const latest = await fetchLatest(8000);
    const native = await nativeVersion();
    if (latest?.version && latest.version !== bundle.version && latest.version !== BUILD_ID
      && !(latest.minNative && isNewer(latest.minNative, native))) {
      target = await bundleFor(latest);
    }
  } catch (e) {
    console.warn('[update] latest download failed, using the downloaded one', e?.message || e);
  }
  await apply(target.id);
  // Сюда доходим, только если установка не удалась (иначе страница уже перезагрузилась).
  if (ui) { ui.label.textContent = t("Не удалось обновить. Попробуйте ещё раз."); ui.btn.disabled = false; ui.btn.style.opacity = ''; }
}

let checking = false;
async function check() {
  if (checking || navigator.onLine === false) return;
  checking = true;
  try {
    const latest = await fetchLatest();
    if (!latest) return;
    const native = await nativeVersion();

    if (latest.minNative && isNewer(latest.minNative, native)) {
      // Новый сайт этому APK не подходит — только предложить новый APK.
      if (latest.apk) offerApk(latest.apk);
      return;
    }
    if (latest.version && latest.version !== BUILD_ID) {
      const bundle = await bundleFor(latest);
      writeJson(PENDING_KEY, { id: bundle.id, version: bundle.version });
      // Одна плашка на все вышедшие версии: новая заменяет прежнюю (showBanner), а кнопка
      // ставит самую свежую на момент нажатия.
      const ui = showBanner(`web:${bundle.version}`, t("Вышло обновление AntonCards"), t("Обновить"), () => updateToLatest(bundle, ui));
      return;
    }
    if (latest.apk && isNewer(latest.apk.version, native)) offerApk(latest.apk);
  } catch (e) {
    console.warn('[update]', e?.message || e);
  } finally {
    checking = false;
  }
}

function offerApk(apk) {
  const b = showBanner(`apk:${apk.version}`, t("Вышла новая версия приложения"), t("Скачать"), () => {
    // Скачивание идёт в браузере телефона (≈ 20 МБ) — раньше в приложении после нажатия
    // ничего не происходило, и казалось, что кнопка сломана. Теперь объясняем, что дальше.
    if (hasAppBridge()) openExternal(apk.url);
    else window.location.href = apk.url; // адрес сайта вне allowNavigation — Capacitor откроет браузер
    if (b) {
      b.label.textContent = t("Загрузка идёт в браузере телефона. Когда скачается — откройте файл AntonCards.apk и нажмите «Установить». Ваши данные сохранятся.");
      b.btn.textContent = t("Ещё раз");
    }
  });
}

async function apply(id) {
  writeJson(PENDING_KEY, null);
  try { await CapacitorUpdater.set({ id }); } catch (e) { console.warn('[update] apply failed', e?.message || e); }
}

export async function startAppUpdates() {
  if (!isNative()) return;
  // Сообщаем плагину, что эта версия запустилась; иначе через 10 с он вернёт прежнюю.
  try { await CapacitorUpdater.notifyAppReady(); } catch {}

  const pending = readJson(PENDING_KEY);
  // Скачанная версия ждёт: включаем сейчас, пока приложение только открылось, — если
  // она последняя (сайт спрашиваем не дольше 2,5 с; нет ответа — ставим её). Флаг
  // снимаем заранее: если версия не запустится, плагин вернёт прежнюю и повторной
  // попытки не будет. При успехе страница перезагрузится и код ниже не выполнится;
  // если версии уже нет (поставили новый APK) — просто идём дальше.
  if (pending && pending.version !== BUILD_ID) {
    const latest = await fetchLatest(2500);
    if (pendingAction(pending, latest?.version, BUILD_ID) === 'apply') await apply(pending.id);
  }
  writeJson(PENDING_KEY, null);

  setTimeout(check, 5000);
  setInterval(check, CHECK_EVERY);
}
